import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MesProductForm } from '@/components/mes-products/MesProductForm'
import {
  useMesProduct,
  useMesProductMutations,
  useMesProductReference,
  usePrimaryDescription,
} from '@/hooks/useMesProducts'
import {
  mesProductDraft,
  mesProductPayload,
  productDescriptions,
  validateMesProduct,
  type MesProductDraft,
  type MesProductFormErrors,
} from '@/lib/mesProducts'
import type { MesProduct } from '@/types/cgt'

/**
 * Add, open or edit a Product Code (z8nrz7fvhx), laid out as the spec's
 * Product Information mock: the four sections side by side, "Last updated ·
 * Audit trail enabled" and Cancel / Save.
 *
 * `productId` null adds one. Opened from an MES run, `mfgNumber` joins the
 * subtitle. Save edits the shared record, so every run naming the code shows
 * the change; without `canEdit` the dialog only shows it.
 */
export function MesProductDialog({
  open,
  onOpenChange,
  productId,
  mfgNumber,
  canEdit,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string | null
  mfgNumber?: string | null
  canEdit: boolean
  onSaved?: (product: MesProduct) => void
}) {
  const creating = productId === null
  const product = useMesProduct(open ? productId : null)
  // Only an editor needs the lists to choose from; a reader sees the values.
  const reference = useMesProductReference(open && canEdit)
  const { create, update } = useMesProductMutations()
  const { value: primary } = usePrimaryDescription()

  const original = useMemo(
    () => (creating ? null : product.data ? mesProductDraft(product.data) : null),
    [creating, product.data],
  )
  const [draft, setDraft] = useState<MesProductDraft>(() => mesProductDraft())
  const [errors, setErrors] = useState<MesProductFormErrors>({})
  const [serverError, setServerError] = useState<string | null>(null)

  // A fresh form each time it opens, and once the product has loaded.
  useEffect(() => {
    if (!open) return
    setDraft(original ?? mesProductDraft())
    setErrors({})
    setServerError(null)
  }, [open, original])

  const adrOther = reference.data?.adrIataOther ?? 'Other'
  const saving = create.isPending || update.isPending
  const loading = !creating && product.isLoading

  function save() {
    const found = validateMesProduct(draft, adrOther)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    setServerError(null)
    const payload = mesProductPayload(draft, original, adrOther)
    const done = {
      onSuccess: (saved: MesProduct) => {
        onSaved?.(saved)
        onOpenChange(false)
      },
      onError: (e: Error) => setServerError(e.message || 'Could not save the Product Code.'),
    }
    if (creating) create.mutate(payload, done)
    else if (Object.keys(payload).length === 0) onOpenChange(false)
    else update.mutate({ id: productId!, payload }, done)
  }

  const subtitle = product.data
    ? [product.data.code, productDescriptions(product.data, primary).primary, mfgNumber].filter(Boolean).join(' · ')
    : null

  return (
    <Dialog open={open} onOpenChange={o => !saving && onOpenChange(o)}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-6xl'>
        <DialogHeader>
          <DialogTitle>{creating ? 'Add Product Code' : 'Product Information'}</DialogTitle>
          <DialogDescription>
            {creating ? (
              'A product an MES order can make. Its code never changes once added.'
            ) : subtitle ? (
              <span className='font-medium text-primary'>{subtitle}</span>
            ) : (
              ' '
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}
        {product.isError && (
          <div className='flex items-center gap-2 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            Could not load the product. {product.error.message}
          </div>
        )}
        {!loading && !product.isError && (
          <MesProductForm
            draft={draft}
            onChange={next => {
              setDraft(next)
              if (Object.keys(errors).length > 0) setErrors({})
            }}
            reference={reference.data}
            errors={errors}
            readOnly={!canEdit}
            creating={creating}
            knownUnits={[product.data?.dedicatedStorageUnit, product.data?.mirrorStorageUnit].filter(
              (u): u is NonNullable<typeof u> => !!u,
            )}
          />
        )}

        {serverError && (
          <div className='flex items-center gap-2 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {serverError}
          </div>
        )}

        <DialogFooter className='items-center sm:justify-between'>
          <p className='text-xs text-muted-foreground'>
            {product.data
              ? `Last updated ${new Date(product.data.updatedAt).toLocaleDateString()} · Audit trail enabled`
              : creating
                ? 'Audit trail enabled'
                : ''}
          </p>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
              {canEdit ? 'Cancel' : 'Close'}
            </Button>
            {canEdit && (
              <Button onClick={save} disabled={saving || loading || product.isError}>
                {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
